import { HeadingText } from '@/components/atoms/HeadingText';
import { Textarea } from '@/components/atoms/Textarea';
import { useFormContext } from '@/components/forms/FormContext';
import {
	type ReplaceVirtuallyFormState,
	StepMap,
	steps,
} from '@/app/replace/virtual/components/ReplaceVirtuallyForm/constants';
import { isValid, SystemIssuesStepActions } from './state';
import { Step } from '@/components/forms/Step';
import { handleNext } from './routing';
import { useValidateStep } from '@/components/forms/useValidateStep';
import { ReplaceVirtuallyFormActionMap } from '@/app/replace/virtual/components/ReplaceVirtuallyForm/reducer';
import { WizardBoxCheckbox } from '@/components/WizardBox';
import cn from 'classnames';
import { ChangeEvent, useState } from 'react';
import { SpaceWow } from '@/components/SpaceWow';
import {
	IconAlarmClock,
	IconLightning,
	IconThermometerChange,
	IconCircleQuestionMark,
	IconDocumentSearch,
} from '@/components/icons';

export interface SystemIssuesStepProps {
	testId?: string;
}

const SystemIssuesStep = (props: SystemIssuesStepProps) => {
	const { testId = 'SystemIssuesStep' } = props;
	const [showOther, setShowOther] = useState(false);
	const { state, dispatch } = useFormContext<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>();
	useValidateStep<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>(isValid, steps.SystemIssues);

	const otherMessage =
		state.systemIssues
			.find((issue) => issue.startsWith('Other:'))
			?.slice(6)
			.trimStart() ?? '';

	const handleIssueSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;
		if (checked && value !== 'Something else') {
			dispatch({
				type: SystemIssuesStepActions.ADD_SYSTEM_ISSUE,
				payload: value,
			});
		} else {
			dispatch({
				type: SystemIssuesStepActions.REMOVE_SYSTEM_ISSUE,
				payload: value,
			});
		}

		if (value === 'Something else') {
			setShowOther(checked);

			if (checked) {
				dispatch({
					type: SystemIssuesStepActions.ADD_SYSTEM_ISSUE,
					payload: 'Other: ',
				});
			} else {
				const issueToRemove =
					state.systemIssues.find((issue) => issue.startsWith('Other:')) ?? '';
				dispatch({
					type: SystemIssuesStepActions.REMOVE_SYSTEM_ISSUE,
					payload: issueToRemove,
				});
			}
		}
	};

	const handleOtherIssueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		dispatch({
			type: SystemIssuesStepActions.UPDATE_OTHER_SYSTEM_ISSUE,
			payload: 'Other: ' + e.target.value,
		});
	};

	return (
		<div data-testid={testId}>
			<Step<StepMap, ReplaceVirtuallyFormState, ReplaceVirtuallyFormActionMap>
				name={steps.SystemIssues}
				onNext={handleNext}
			>
				<HeadingText
					tag="h1"
					size="h2"
					className="mb-3 mt-10 lg:mb-4 lg:mt-20"
					balanced
				>
					What&rsquo;s going on with your current system?
				</HeadingText>

				<p className="text-center text-xl tracking-tighter text-black lg:text-2xl">
					Choose as many as you like
				</p>

				<SpaceWow spacer_10 lg__spacer_14 />

				<div className="mx-auto flex max-w-5xl flex-wrap place-content-center gap-4 lg:gap-6">
					<WizardBoxCheckbox
						id="system-age"
						name="system-age"
						value="System age"
						label="System age"
						description="It’s time to replace my system"
						icon={
							<IconAlarmClock className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('System age')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="performance-issues"
						name="performance-issues"
						value="Performance issues"
						label="Performance issues"
						description="Advised by contractor to replace"
						icon={
							<IconThermometerChange className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Performance issues')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="not-currently-working"
						name="not-currently-working"
						value="Not currently working"
						label="Not currently working"
						description="My system is not working"
						icon={
							<IconLightning className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Not currently working')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="being-proactive"
						name="being-proactive"
						value="Being proactive"
						label="Being proactive"
						description="I’m doing research to be proactive"
						icon={
							<IconDocumentSearch className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Being proactive')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="something-else"
						name="something-else"
						value="Something else"
						label="Something else"
						description="My issue isn’t listed here"
						icon={
							<IconCircleQuestionMark className="h-auto w-full max-w-8 fill-black lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={showOther}
						className="w-full lg:max-w-70"
					/>
				</div>
				<div className={cn({ hidden: !showOther })}>
					<SpaceWow spacer_08 lg__spacer_10 />
					<Textarea
						id="other"
						name="other"
						label="Tell us what’s going on"
						onChange={handleOtherIssueChange}
						value={otherMessage}
						fieldsetClassName="mx-auto lg:max-w-101"
					/>
				</div>
			</Step>
		</div>
	);
};

export default SystemIssuesStep;
